// vg-binding: P7.D-163
// vg-binding: P7.D-16
//
// apps/vendor/src/features/products/variants/VariantOptionSelector.tsx
//
// Amendment #4 — catalog-path variant builder. Replaces the
// free-type size/color grid (VariantMatrix) when the vendor picks a blueprint.
// Given the blueprint detail (optionAxes + variants[]), the vendor multi-selects
// option values per axis; the component computes the cartesian of chosen values,
// matches each combo to a blueprint.variants[] row by optionValueIds set-equality
// to resolve a read-only SKU + default dimensions + default weight, and emits
// VariantDraft[]. The vendor edits only cost_markup_cents, image_urls,
// external_sku, and per-SKU processing/capacity/weight overrides; each SKU has
// an enable/disable toggle (markup unchanged).

import { useMemo } from 'react';

import { useTranslation } from '../../../lib/i18n/index.js';
import {
  type CatalogBlueprintDetail,
  type LocalizedName,
} from '../api.js';
import type { VariantDraft } from './types.js';

interface Props {
  detail: CatalogBlueprintDetail;
  value: VariantDraft[];
  onChange: (next: VariantDraft[]) => void;
  /** Chosen option-value ids per axis (controlled by the parent page). */
  chosen: Record<string, string[]>;
  onChosenChange: (next: Record<string, string[]>) => void;
}

function localize(name: LocalizedName, lang: string): string {
  if (typeof name === 'string') return name;
  if (!name) return '';
  const short = (lang || 'en').slice(0, 2).toLowerCase() as 'en' | 'vi' | 'fr';
  return (
    name[short] ??
    name.en ??
    name.vi ??
    name.fr ??
    Object.values(name).find((v) => typeof v === 'string' && v.length > 0) ??
    ''
  );
}

/** Cartesian product of arrays. [[a,b],[c]] → [[a,c],[b,c]]. */
function cartesian(arrays: string[][]): string[][] {
  return arrays.reduce<string[][]>(
    (acc, cur) => acc.flatMap((combo) => cur.map((v) => [...combo, v])),
    [[]],
  );
}

export function VariantOptionSelector({
  detail,
  value,
  onChange,
  chosen,
  onChosenChange,
}: Props): JSX.Element {
  const { t, i18n } = useTranslation('products');
  const lang = i18n.language;

  // Index blueprint variants by sorted optionValueIds key for O(1) match.
  const variantByKey = useMemo(() => {
    const m = new Map<
      string,
      { sku: string; dimensions: { length: number; width: number; height: number }; weightGrams: number }
    >();
    for (const v of detail.variants) {
      const key = [...v.optionValueIds].sort().join('|');
      m.set(key, { sku: v.sku, dimensions: v.dimensions, weightGrams: v.weightGrams });
    }
    return m;
  }, [detail.variants]);

  // Preserve already-entered editable values across selection changes (key by sku).
  const draftBySku = useMemo(() => {
    const m = new Map<string, VariantDraft>();
    for (const d of value) if (d.sku) m.set(d.sku, d);
    return m;
  }, [value]);

  function toggleValue(axisId: string, valueId: string): void {
    const cur = chosen[axisId] ?? [];
    const next = cur.includes(valueId)
      ? cur.filter((v) => v !== valueId)
      : [...cur, valueId];
    const nextChosen = { ...chosen, [axisId]: next };
    onChosenChange(nextChosen);
    regenerate(nextChosen);
  }

  function regenerate(nextChosen: Record<string, string[]>): void {
    // Only axes with ≥1 chosen value participate in the cartesian.
    const axisLists = detail.optionAxes
      .map((axis) => nextChosen[axis.optionId] ?? [])
      .filter((list) => list.length > 0);
    if (axisLists.length === 0) {
      onChange([]);
      return;
    }
    const combos = cartesian(axisLists);
    const out: VariantDraft[] = [];
    for (const combo of combos) {
      const key = [...combo].sort().join('|');
      const match = variantByKey.get(key);
      if (!match) continue; // combo has no blueprint variant — skip
      const existing = draftBySku.get(match.sku);
      // Derive a human-ish size/color label from the chosen option-value codes.
      const labels = combo.map((ovId) => {
        for (const axis of detail.optionAxes) {
          const found = axis.values.find((v) => v.optionValueId === ovId);
          if (found) return found.code;
        }
        return ovId;
      });
      out.push(
        existing ?? {
          size: labels[0] ?? 'Default',
          color: labels[1] ?? labels[0] ?? 'Default',
          enabled: true,
          cost_markup_cents: 0,
          image_urls: [],
          dimensions: { ...match.dimensions },
          processing_time_days: 0,
          capacity_per_day: 0,
          weight_grams: match.weightGrams,
          external_sku: '',
          option_value_ids: combo,
          sku: match.sku,
        },
      );
    }
    onChange(out);
  }

  function patchDraft(sku: string, patch: Partial<VariantDraft>): void {
    onChange(value.map((d) => (d.sku === sku ? { ...d, ...patch } : d)));
  }

  return (
    <section
      data-testid="variant-option-selector"
      className="rounded-xl border border-pw-border bg-pw-bg p-4"
    >
      <h3 className="text-sm font-semibold text-pw-text">
        {t('optionSelector.title')}
      </h3>
      <p className="mt-1 text-xs text-pw-text-muted">
        {t('optionSelector.description')}
      </p>

      {/* Axis multi-selects */}
      <div className="mt-3 flex flex-col gap-3">
        {detail.optionAxes.map((axis) => (
          <div key={axis.optionId} data-testid={`option-axis-${axis.code}`}>
            <p className="mb-1 text-sm font-medium text-pw-text">
              {localize(axis.name, lang)}
            </p>
            <div className="flex flex-wrap gap-2">
              {axis.values.map((ov) => {
                const isOn = (chosen[axis.optionId] ?? []).includes(
                  ov.optionValueId,
                );
                return (
                  <button
                    key={ov.optionValueId}
                    type="button"
                    data-testid={`option-value-${ov.code}`}
                    onClick={() => toggleValue(axis.optionId, ov.optionValueId)}
                    className={`rounded-md border px-3 py-1 text-sm transition-colors ${
                      isOn
                        ? 'border-pw-primary bg-pw-primary/10 text-pw-text'
                        : 'border-pw-border bg-pw-bg text-pw-text-muted hover:bg-pw-primary/5'
                    }`}
                  >
                    {localize(ov.name, lang)}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Generated variants */}
      {value.length > 0 ? (
        <div className="mt-4 overflow-x-auto rounded-md border border-pw-border">
          <table className="w-full text-left text-xs" data-testid="generated-variants">
            <thead className="bg-pw-surface text-pw-text-muted">
              <tr>
                <th className="px-2 py-1">{t('optionSelector.colEnabled')}</th>
                <th className="px-2 py-1">SKU</th>
                <th className="px-2 py-1">{t('optionSelector.colMarkup')}</th>
                <th className="px-2 py-1">{t('variants.fields.processingTime')}</th>
                <th className="px-2 py-1">{t('variants.fields.capacityPerDay')}</th>
                <th className="px-2 py-1">{t('variants.fields.weightGrams')}</th>
                <th className="px-2 py-1">{t('optionSelector.colDimensions')}</th>
                <th className="px-2 py-1">{t('variants.fields.externalSku')}</th>
              </tr>
            </thead>
            <tbody>
              {value.map((d) => (
                <tr
                  key={d.sku}
                  data-testid={`generated-variant-${d.sku}`}
                  className="border-t border-pw-border"
                >
                  <td className="px-2 py-1">
                    <input
                      type="checkbox"
                      checked={d.enabled}
                      onChange={(e) =>
                        patchDraft(d.sku!, { enabled: e.target.checked })
                      }
                      data-testid={`generated-enable-${d.sku}`}
                    />
                  </td>
                  <td className="px-2 py-1 font-mono text-pw-text-muted">{d.sku}</td>
                  <td className="px-2 py-1">
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      value={(d.cost_markup_cents / 100).toFixed(2)}
                      onChange={(e) => {
                        const v = Number.parseFloat(e.target.value);
                        patchDraft(d.sku!, {
                          cost_markup_cents: Number.isFinite(v)
                            ? Math.max(0, Math.round(v * 100))
                            : 0,
                        });
                      }}
                      disabled={!d.enabled}
                      className="h-7 w-16 rounded border border-pw-border bg-pw-bg px-1"
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      type="number"
                      step="1"
                      min={0}
                      value={d.processing_time_days}
                      onChange={(e) =>
                        patchDraft(d.sku!, {
                          processing_time_days: Math.max(
                            0,
                            Number.parseInt(e.target.value, 10) || 0,
                          ),
                        })
                      }
                      disabled={!d.enabled}
                      className="h-7 w-14 rounded border border-pw-border bg-pw-bg px-1"
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      type="number"
                      step="1"
                      min={0}
                      value={d.capacity_per_day}
                      onChange={(e) =>
                        patchDraft(d.sku!, {
                          capacity_per_day: Math.max(
                            0,
                            Number.parseInt(e.target.value, 10) || 0,
                          ),
                        })
                      }
                      disabled={!d.enabled}
                      className="h-7 w-14 rounded border border-pw-border bg-pw-bg px-1"
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      type="number"
                      step="1"
                      min={0}
                      value={d.weight_grams}
                      onChange={(e) =>
                        patchDraft(d.sku!, {
                          weight_grams: Math.max(
                            0,
                            Number.parseFloat(e.target.value) || 0,
                          ),
                        })
                      }
                      disabled={!d.enabled}
                      className="h-7 w-14 rounded border border-pw-border bg-pw-bg px-1"
                    />
                  </td>
                  <td className="px-2 py-1 text-pw-text-muted">
                    {d.dimensions.length}×{d.dimensions.width}×{d.dimensions.height} cm
                  </td>
                  <td className="px-2 py-1">
                    <input
                      type="text"
                      maxLength={64}
                      value={d.external_sku ?? ''}
                      onChange={(e) =>
                        patchDraft(d.sku!, { external_sku: e.target.value })
                      }
                      disabled={!d.enabled}
                      placeholder={t('variants.fields.externalSku')}
                      className="h-7 w-28 rounded border border-pw-border bg-pw-bg px-1"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="mt-4 text-xs text-pw-text-muted" data-testid="no-variants-yet">
          {t('optionSelector.emptyHint')}
        </p>
      )}
    </section>
  );
}

export default VariantOptionSelector;
